/**
 * useTranslationCatalog — runtime overlay of the DB-backed translation catalog.
 *
 * The bundled `en.json` (plus `fr.json` during migration) is the synchronous baseline
 * fed to `IntlProvider` at mount. This hook fetches the tenant/workspace-scoped catalog
 * from `GET /api/v1/translations` and returns the active-locale strings so the caller
 * can overlay them on the baseline:
 *
 *     effective = { ...baseline_json, ...catalog.messages }
 *
 * Because the catalog is scoped by (tenantId, workspaceId) on the backend, its values
 * are already the per-tenant effective strings — no separate override fetch is needed.
 * Results are cached in localStorage per (locale, tenant, workspace) so returning users
 * hydrate instantly and revalidate in the background.
 *
 * The catalog stores `item.key` as the full dotted message id (e.g. "nav.modules"),
 * matching the IntlProvider message keys, so no key reconstruction is required.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { listTranslations, TranslationsApiError } from '@/uptk/core/api/TranslationsApi';
import type { TranslationItem } from '@/types/translations';
import {
  getAuthContextState,
  subscribeAuthContextState,
} from '@/uptk/core/api/auth_context_store';

export type TranslationCatalogSource = 'backend' | 'cache' | 'empty';

export type TranslationCatalogState = {
  /** Active-locale strings from the catalog, keyed by full dotted message id. */
  messages: Record<string, string>;
  /** Whether a fetch is in flight. */
  loading: boolean;
  /** Where the current messages came from. */
  source: TranslationCatalogSource;
};

const CACHE_PREFIX = 'i18n_catalog_';

const buildCacheKey = (locale: string, tenantId: string, workspaceId: string): string =>
  `${CACHE_PREFIX}${tenantId || '*'}:${workspaceId || '*'}:${locale}`;

/** Extract the active-locale value for each catalog item into a flat id→string map. */
const flattenCatalog = (items: TranslationItem[], locale: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const item of items) {
    const value = item.values?.[locale];
    if (typeof value === 'string' && value.length > 0) {
      const key = item.key?.trim();
      if (key) out[key] = value;
    }
  }
  return out;
};

const readCache = (key: string): Record<string, string> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : null;
  } catch {
    return null;
  }
};

const writeCache = (key: string, messages: Record<string, string>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(messages));
  } catch {
    // Ignore quota / serialization failures — the baseline still renders.
  }
};

// Module-level guard: if the translations endpoint is unavailable (404 / network / not
// deployed), stop re-hitting it for the rest of the session. Reset on reload.
let catalogEndpointUnavailable = false;

export function resetTranslationCatalogAvailability(): void {
  catalogEndpointUnavailable = false;
}

export function useTranslationCatalog(locale: string): TranslationCatalogState {
  const [authContext, setAuthContext] = useState(() => getAuthContextState());

  useEffect(() => subscribeAuthContextState(setAuthContext), []);

  const tenantId = authContext?.tenantId ?? '';
  const workspaceId = authContext?.workspaceId ?? '';
  const cacheKey = buildCacheKey(locale, tenantId, workspaceId);

  const [state, setState] = useState<TranslationCatalogState>(() => {
    const cached = readCache(cacheKey);
    return cached
      ? { messages: cached, loading: true, source: 'cache' }
      : { messages: {}, loading: true, source: 'empty' };
  });

  const isActiveRef = useRef(true);

  const load = useCallback(async () => {
    // No workspace context yet, or the endpoint is known-unavailable: render the
    // bundled baseline only (empty overlay).
    if (!workspaceId || catalogEndpointUnavailable) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const catalog = await listTranslations(
        workspaceId,
        undefined,
        { tenantId: tenantId || undefined }
      );
      if (!isActiveRef.current) return;
      const messages = flattenCatalog(catalog.items, locale);
      writeCache(cacheKey, messages);
      setState({ messages, loading: false, source: 'backend' });
    } catch (error) {
      if (!isActiveRef.current) return;
      if (
        error instanceof TranslationsApiError &&
        (error.status === 404 || error.status === null)
      ) {
        catalogEndpointUnavailable = true;
      }
      const cached = readCache(cacheKey);
      setState({
        messages: cached ?? {},
        loading: false,
        source: cached ? 'cache' : 'empty',
      });
    }
  }, [cacheKey, locale, tenantId, workspaceId]);

  useEffect(() => {
    isActiveRef.current = true;
    // Re-seed synchronously from cache when the key changes (locale / tenant switch)
    // so the overlay never flashes empty for a previously-loaded locale.
    const cached = readCache(cacheKey);
    setState((prev) => ({
      messages: cached ?? prev.messages,
      loading: true,
      source: cached ? 'cache' : prev.source,
    }));
    void load();
    return () => {
      isActiveRef.current = false;
    };
  }, [cacheKey, load]);

  return state;
}

export default useTranslationCatalog;
